import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth, useUserMap } from "@/context/AuthContext";
import { inr, pct, fmtDate, fmtDateLong, monthLabel, currentMonth } from "@/lib/format";
import { Badge, Bar } from "@/components/Badge";
import {
  CONTENT_STATUS, CLIENT_STATUS, HEALTH, SHOOT_STATUS, PAYMENT_STATUS, BILLING_CYCLES, BILLING_TYPES, PAYMENT_METHODS,
  ASSIGN_ROLES, JOB_ROLE_LABEL, label,
} from "@/lib/meta";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Loader2, Plus, Pencil, Phone, Mail, MapPin, Instagram, Globe, Calendar, FileText, UserPlus, X, Users,
} from "lucide-react";
import { toast } from "sonner";
import { ContentDialog } from "@/pages/ContentCalendar";
import { RecordPaymentDialog } from "@/pages/Payments";
import { ClientDialog } from "@/pages/Clients";

function TeamTab({ cid, team, onChange, isManager }) {
  const { users } = useAuth();
  const [uid, setUid] = useState("");
  const [role, setRole] = useState("social_media_handler");
  const [saving, setSaving] = useState(false);
  const active = users.filter((u) => u.active);

  const add = async () => {
    if (!uid) return toast.error("Select an employee");
    setSaving(true);
    try {
      await api.post(`/clients/${cid}/assignments`, { user_id: uid, role });
      toast.success("Employee assigned");
      setUid("");
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not assign"); } finally { setSaving(false); }
  };
  const remove = async (member) => {
    await api.delete(`/clients/${cid}/assignments`, { params: { user_id: member.user_id, role: member.role } });
    toast.success("Removed");
    onChange();
  };

  return (
    <Card className="rounded-lg border-zinc-200 p-5 shadow-sm" data-testid="client-team-tab">
      <h3 className="font-heading text-base font-semibold">Assigned Team</h3>
      <p className="mt-0.5 text-sm text-zinc-500">Multiple employees can work on this client. Assignment gives workspace visibility — it is separate from who owns each content item.</p>

      {isManager && (
        <div className="mt-4 flex flex-wrap items-end gap-3 rounded-md bg-zinc-50 p-4">
          <div className="min-w-[200px] flex-1">
            <label className="text-xs text-zinc-500">Employee</label>
            <Select value={uid} onValueChange={setUid}>
              <SelectTrigger className="mt-1 bg-white" data-testid="assign-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>{active.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {JOB_ROLE_LABEL[u.job_role] || u.job_role}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="w-52">
            <label className="text-xs text-zinc-500">Role on this client</label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="mt-1 bg-white" data-testid="assign-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ASSIGN_ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={add} disabled={saving} data-testid="assign-employee-button">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />}Assign</Button>
        </div>
      )}

      <div className="mt-4 space-y-2">
        {(!team || team.length === 0) && <div className="py-8 text-center text-sm text-zinc-400">No team members assigned yet</div>}
        {(team || []).map((m) => (
          <div key={`${m.user_id}-${m.role}`} className="flex items-center justify-between rounded-md border border-zinc-100 px-3 py-2.5" data-testid={`team-member-${m.user_id}`}>
            <div className="flex items-center gap-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold text-white" style={{ background: m.color || "#71717a" }}>{(m.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("")}</span>
              <div><div className="text-sm font-medium">{m.name}</div><div className="text-xs text-zinc-500">{label({ [m.role]: { label: ASSIGN_ROLES.find((r) => r.v === m.role)?.l || m.role } }, m.role)}</div></div>
            </div>
            {isManager && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(m)} data-testid={`remove-member-${m.user_id}`}><X className="h-4 w-4" /></Button>}
          </div>
        ))}
      </div>
    </Card>
  );
}

function Metric({ label: l, value, tone }) {
  const c = { green: "text-emerald-600", orange: "text-amber-600", red: "text-red-600" }[tone] || "text-zinc-900";
  return (
    <Card className="rounded-lg border-zinc-200 p-4 shadow-sm">
      <div className="text-xs font-medium uppercase tracking-wide text-zinc-500">{l}</div>
      <div className={`metric-value mt-1.5 text-xl font-semibold ${c}`}>{value}</div>
    </Card>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { isManager } = useAuth();
  const umap = useUserMap();
  const [contentOpen, setContentOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [shootView, setShootView] = useState(null);

  const { data, isLoading } = useQuery({ queryKey: ["client", id], queryFn: async () => (await api.get(`/clients/${id}`)).data });
  const { data: content = [] } = useQuery({ queryKey: ["content", currentMonth(), id], queryFn: async () => (await api.get("/content", { params: { month: currentMonth(), client_id: id } })).data });
  const { data: activity = [] } = useQuery({ queryKey: ["activity", id], queryFn: async () => (await api.get("/activity", { params: { entity_id: id } })).data });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["client", id] });
    qc.invalidateQueries({ queryKey: ["content"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  };

  if (isLoading || !data)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>;

  const c = data.client;
  const h = data.health;
  const canPay = data.can_view_payments;
  const commit = c.commitment || {};
  const commitRows = [["Static Posts", commit.static], ["Carousels", commit.carousel], ["Reels", commit.reel], ["Videos", commit.video], ["Stories", commit.story], ["Shoot Days", commit.shoot]].filter(([, v]) => v > 0);

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      <button onClick={() => navigate("/clients")} className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900"><ArrowLeft className="h-4 w-4" />Back to Clients</button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-zinc-900 font-heading text-lg font-bold text-white">{c.name.slice(0, 2).toUpperCase()}</div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">{c.name}</h1>
              <Badge color={CLIENT_STATUS[c.status]?.color} dot>{label(CLIENT_STATUS, c.status)}</Badge>
            </div>
            <p className="mt-1 text-sm text-zinc-500">{c.package || "—"} · Since {fmtDateLong(c.start_date)}</p>
          </div>
        </div>
        {isManager && <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-client-button"><Pencil className="mr-2 h-4 w-4" />Edit</Button>}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="rounded-lg border-zinc-200 p-4 shadow-sm">
          <div className="text-xs font-medium uppercase tracking-wide text-zinc-500">Content Progress</div>
          <div className="mt-1.5 flex items-center gap-2">
            <span className="metric-value text-xl font-semibold">{pct(h.actual_progress)}</span>
            <Badge color={h.health}>{HEALTH[h.health].label}</Badge>
          </div>
          <div className="mt-2"><Bar value={h.actual_progress} color={h.health} /></div>
        </Card>
        <Metric l={`${BILLING_CYCLES.find((b) => b.v === c.billing_cycle)?.l || "Cycle"} Billing`} value={canPay ? inr(c.monthly_amount) : "—"} />
        {canPay
          ? <Metric l="Outstanding" value={inr(data.outstanding)} tone={data.outstanding > 0 ? "red" : "green"} />
          : <Metric l="Team" value={(data.team || []).length} tone="blue" />}
        <Metric l="Upcoming Shoot" value={data.upcoming_shoot ? fmtDate(data.upcoming_shoot.date) : "—"} tone="blue" />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap" data-testid="client-tabs">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="team">Team</TabsTrigger>
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="shoots">Shoots</TabsTrigger>
          {canPay && <TabsTrigger value="billing">Billing</TabsTrigger>}
          {canPay && <TabsTrigger value="payments">Payments</TabsTrigger>}
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card className="rounded-lg border-zinc-200 p-5 shadow-sm">
              <h3 className="font-heading text-base font-semibold">Contact</h3>
              <div className="mt-3 space-y-2 text-sm">
                <div className="flex items-center gap-2 text-zinc-600"><span className="w-28 text-zinc-400">Contact Person</span>{c.contact_person || "—"}</div>
                <div className="flex items-center gap-2 text-zinc-600"><Phone className="h-4 w-4 text-zinc-400" />{c.phone || "—"}</div>
                <div className="flex items-center gap-2 text-zinc-600"><Mail className="h-4 w-4 text-zinc-400" />{c.email || "—"}</div>
                <div className="flex items-center gap-2 text-zinc-600"><Instagram className="h-4 w-4 text-zinc-400" />{c.instagram || "—"}</div>
                <div className="flex items-center gap-2 text-zinc-600"><Globe className="h-4 w-4 text-zinc-400" />{c.website || "—"}</div>
                <div className="flex items-center gap-2 text-zinc-600"><MapPin className="h-4 w-4 text-zinc-400" />{c.address || "—"}</div>
                <div className="flex items-center gap-2 text-zinc-600"><span className="w-28 text-zinc-400">Account Manager</span>{umap[c.account_manager_id]?.name || "—"}</div>
              </div>
            </Card>
            <Card className="rounded-lg border-zinc-200 p-5 shadow-sm">
              <h3 className="font-heading text-base font-semibold">Monthly Commitment</h3>
              <div className="mt-3 space-y-2">
                {commitRows.map(([l, v]) => (
                  <div key={l} className="flex items-center justify-between text-sm">
                    <span className="text-zinc-600">{l}</span>
                    <span className="font-mono font-medium">{v}</span>
                  </div>
                ))}
                {commitRows.length === 0 && <div className="text-sm text-zinc-400">No commitment set</div>}
                <div className="flex items-center justify-between border-t border-zinc-100 pt-2 text-sm font-semibold">
                  <span>Posted This Week</span><span className="font-mono">{h.posted}/{h.planned}</span>
                </div>
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* TEAM */}
        <TabsContent value="team" className="mt-4">
          <TeamTab cid={id} team={data.team} onChange={refresh} isManager={isManager} />
        </TabsContent>

        {/* CONTENT */}
        <TabsContent value="content" className="mt-4">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm text-zinc-500">{monthLabel(currentMonth())} · {content.length} items</span>
            <Button size="sm" onClick={() => setContentOpen(true)} data-testid="client-add-content"><Plus className="mr-1.5 h-4 w-4" />Add Content</Button>
          </div>
          <Card className="overflow-hidden rounded-lg border-zinc-200 shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-zinc-100 bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
                  <th className="px-4 py-3 text-left font-medium">Title</th>
                  <th className="px-4 py-3 text-left font-medium">Date</th>
                  <th className="px-4 py-3 text-left font-medium">Type</th>
                  <th className="px-4 py-3 text-left font-medium">Assigned</th>
                  <th className="px-4 py-3 text-left font-medium">Status</th>
                </tr></thead>
                <tbody>
                  {content.map((it) => (
                    <tr key={it.id} className="border-b border-zinc-50 hover:bg-zinc-50">
                      <td className="px-4 py-2.5 font-medium text-zinc-900">{it.title}</td>
                      <td className="px-4 py-2.5 text-zinc-600">{fmtDate(it.date)}</td>
                      <td className="px-4 py-2.5 capitalize text-zinc-600">{it.content_type}</td>
                      <td className="px-4 py-2.5 text-zinc-600">{umap[it.assigned_to]?.name || "—"}</td>
                      <td className="px-4 py-2.5"><Badge color={CONTENT_STATUS[it.status]?.color}>{label(CONTENT_STATUS, it.status)}</Badge></td>
                    </tr>
                  ))}
                  {content.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-zinc-400">No content this month</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* SHOOTS */}
        <TabsContent value="shoots" className="mt-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {data.shoots.map((s) => (
              <Card key={s.id} className="rounded-lg border-zinc-200 p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="font-medium">{s.shoot_type}</div>
                  <Badge color={SHOOT_STATUS[s.status]?.color} dot>{label(SHOOT_STATUS, s.status)}</Badge>
                </div>
                <div className="mt-2 text-xs text-zinc-500">{fmtDateLong(s.date)} · {s.start_time}–{s.end_time}</div>
                <div className="mt-1 text-xs text-zinc-500">{s.location || "—"}</div>
              </Card>
            ))}
            {data.shoots.length === 0 && <div className="col-span-full py-8 text-center text-zinc-400">No shoots yet</div>}
          </div>
        </TabsContent>

        {/* BILLING */}
        <TabsContent value="billing" className="mt-4 space-y-4">
          {data.client.service_start_date ? null : (
            <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="billing-start-missing">
              <FileText className="h-4 w-4" /> Billing Start Date not set — only the current period is billed. Set it in Edit Client to generate the full schedule.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Metric l="Cycle Amount" value={inr(c.monthly_amount)} />
            <Metric l="Outstanding" value={inr(data.billing_summary?.outstanding || 0)} tone={(data.billing_summary?.outstanding || 0) > 0 ? "red" : "green"} />
            <Metric l="Overdue Periods" value={data.billing_summary?.overdue_periods || 0} tone={(data.billing_summary?.overdue_periods || 0) > 0 ? "red" : "green"} />
            <Metric l="Oldest Due" value={data.billing_summary?.oldest_due_date ? fmtDate(data.billing_summary.oldest_due_date) : "—"} />
            <Metric l="Advance Credit" value={inr(data.billing_summary?.credit || 0)} tone="blue" />
            <Metric l="Last Payment" value={data.billing_summary?.last_payment ? inr(data.billing_summary.last_payment.amount) : "—"} />
          </div>
          <Card className="rounded-lg border-zinc-200 p-5 shadow-sm">
            <h3 className="font-heading text-base font-semibold">Billing Profile</h3>
            <div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
              {[
                ["Cycle Amount", inr(c.monthly_amount)],
                ["Billing Cycle", BILLING_CYCLES.find((b) => b.v === c.billing_cycle)?.l || c.billing_cycle],
                ["Billing Basis", c.billing_basis === "arrears" ? "Arrears (after period)" : "Advance (upfront)"],
                ["Service Start Date", c.service_start_date ? fmtDateLong(c.service_start_date) : "Not set"],
                ["Billing Date", `${c.billing_day} of the cycle`],
                ["Payment Method", PAYMENT_METHODS.find((m) => m.v === c.payment_method)?.l || c.payment_method],
                ["Billing Active", c.billing_active === false ? "No" : "Yes"],
                ["GSTIN", c.gstin || "—"],
              ].map(([l, v]) => (
                <div key={l}>
                  <div className="text-xs font-medium uppercase tracking-wide text-zinc-400">{l}</div>
                  <div className="mt-0.5 text-sm font-medium text-zinc-900">{v}</div>
                </div>
              ))}
            </div>
            {c.billing_notes && <p className="mt-4 rounded-md bg-zinc-50 p-3 text-sm text-zinc-600">{c.billing_notes}</p>}
          </Card>
        </TabsContent>

        {/* PAYMENTS — billing ledger + receipts */}
        <TabsContent value="payments" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-zinc-600">Billing Ledger</span>
            {isManager && <Button size="sm" onClick={() => setPayOpen(true)} data-testid="client-record-payment"><Plus className="mr-1.5 h-4 w-4" />Record Payment</Button>}
          </div>
          <Card className="overflow-hidden rounded-lg border-zinc-200 shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-zinc-100 bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
                  <th className="px-4 py-3 text-left font-medium">Period</th>
                  <th className="px-4 py-3 text-right font-medium">Amount Due</th>
                  <th className="px-4 py-3 text-left font-medium">Due Date</th>
                  <th className="px-4 py-3 text-right font-medium">Paid</th>
                  <th className="px-4 py-3 text-right font-medium">Balance</th>
                  <th className="px-4 py-3 text-left font-medium">Status</th>
                </tr></thead>
                <tbody>
                  {(data.billing_history || []).map((p) => (
                    <tr key={p.id} data-testid={`invoice-row-${p.id}`} className="border-b border-zinc-50 hover:bg-zinc-50">
                      <td className="px-4 py-2.5 font-medium">{p.label}</td>
                      <td className="px-4 py-2.5 text-right font-mono">{inr(p.amount_due)}</td>
                      <td className="px-4 py-2.5 text-zinc-600">{fmtDate(p.due_date)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-emerald-600">{inr(p.allocated)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-red-600">{p.balance > 0 ? inr(p.balance) : "—"}</td>
                      <td className="px-4 py-2.5"><Badge color={PAYMENT_STATUS[p.status]?.color} dot>{label(PAYMENT_STATUS, p.status)}</Badge></td>
                    </tr>
                  ))}
                  {(data.billing_history || []).length === 0 && <tr><td colSpan={6} className="py-8 text-center text-zinc-400">No billing periods yet — set a Service Start Date & amount</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>

          <span className="text-sm font-medium text-zinc-600">Payment Receipts</span>
          <Card className="overflow-hidden rounded-lg border-zinc-200 shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-zinc-100 bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
                  <th className="px-4 py-3 text-left font-medium">Date</th>
                  <th className="px-4 py-3 text-right font-medium">Amount</th>
                  <th className="px-4 py-3 text-left font-medium">Method</th>
                  <th className="px-4 py-3 text-left font-medium">Reference</th>
                  <th className="px-4 py-3 text-right font-medium">Unallocated</th>
                </tr></thead>
                <tbody>
                  {(data.billing_summary?.payments || []).map((p) => (
                    <tr key={p.id} className="border-b border-zinc-50 hover:bg-zinc-50">
                      <td className="px-4 py-2.5 text-zinc-600">{fmtDate(p.payment_date)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-emerald-600">{inr(p.amount)}</td>
                      <td className="px-4 py-2.5 text-zinc-600">{PAYMENT_METHODS.find((m) => m.v === p.method)?.l || p.method}</td>
                      <td className="px-4 py-2.5 font-mono text-xs text-zinc-500">{p.reference || "—"}{p.migrated && <span className="ml-1 text-[10px] uppercase text-zinc-400">imported</span>}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-blue-600">{p.credit > 0 ? inr(p.credit) : "—"}</td>
                    </tr>
                  ))}
                  {(data.billing_summary?.payments || []).length === 0 && <tr><td colSpan={5} className="py-8 text-center text-zinc-400">No payments recorded</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* DOCUMENTS */}
        <TabsContent value="documents" className="mt-4">
          <Card className="flex flex-col items-center justify-center rounded-lg border-dashed border-zinc-300 py-16 text-center">
            <FileText className="h-8 w-8 text-zinc-300" />
            <p className="mt-3 text-sm font-medium text-zinc-500">Document storage arrives in Phase 2</p>
            <p className="mt-1 text-xs text-zinc-400">Contracts, brand assets and receipts will live here.</p>
          </Card>
        </TabsContent>

        {/* ACTIVITY */}
        <TabsContent value="activity" className="mt-4">
          <Card className="rounded-lg border-zinc-200 p-5 shadow-sm">
            <div className="space-y-3">
              {activity.map((a) => (
                <div key={a.id} className="flex items-start gap-3 border-b border-zinc-50 pb-3 last:border-0">
                  <Calendar className="mt-0.5 h-4 w-4 text-zinc-400" />
                  <div className="flex-1">
                    <div className="text-sm text-zinc-900">{a.action}</div>
                    <div className="text-xs text-zinc-400">{a.user_name} · {new Date(a.created_at).toLocaleString("en-IN")}</div>
                  </div>
                </div>
              ))}
              {activity.length === 0 && <div className="py-8 text-center text-zinc-400">No activity yet</div>}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <ContentDialog open={contentOpen} onOpenChange={setContentOpen} defaultClient={id} onSaved={refresh} />
      <RecordPaymentDialog open={payOpen} onOpenChange={setPayOpen} defaultClient={id} onSaved={refresh} />
      <ClientDialog open={editOpen} onOpenChange={setEditOpen} initial={c} onSaved={refresh} />
    </div>
  );
}
